import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { IconButton } from '@mui/material';
import { Tooltip } from 'antd';
import clsx from 'clsx';
import { RouteHistoryItem } from 'components/AppMainNavigationTabBar/AppMainNavigationTabBar';
import ConditionalWrap from 'components/ConditionalWrap/ConditionalWrap';
import { CSSProperties, memo, MouseEvent, useMemo } from 'react';

type SortableTabProps = {
  route: RouteHistoryItem;
  isActiveTab: boolean;
  tabURL: string;
  onNavigate: (url: string) => void;
  onRemove: (e: MouseEvent<HTMLButtonElement>, pathName: string) => void;
};

const SortableTab = ({
  route,
  isActiveTab,
  tabURL,
  onNavigate,
  onRemove,
}: SortableTabProps) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useSortable({ id: route.pathname });

  const style: CSSProperties = useMemo(
    () => ({
      transform: CSS.Transform.toString(transform),
      transition: 'none',
      willChange: 'transform',
      touchAction: 'none',
      zIndex: isDragging ? 50 : undefined,
      opacity: isDragging ? 0.9 : 1,
    }),
    [isDragging, transform]
  );

  return (
    <li
      ref={setNodeRef}
      style={style}
      onClick={() => onNavigate(tabURL)}
      className={clsx(
        'relative cursor-pointer select-none rounded-xl bg-gray-100 p-2 pl-3 transition-all',
        isActiveTab ? '!bg-primary' : 'hover:bg-gray-200',
        isDragging && 'shadow-lg'
      )}
      {...attributes}
      {...listeners}
    >
      <ConditionalWrap
        condition={!isDragging}
        wrap={children => (
          <Tooltip
            title={route.title}
            placement="bottomLeft"
            destroyTooltipOnHide
          >
            {children}
          </Tooltip>
        )}
      >
        <p
          className={clsx(
            'line-clamp-1 w-30 truncate',
            isActiveTab ? 'pr-1 !font-semibold text-white' : 'pr-6'
          )}
        >
          {route.title}
        </p>
      </ConditionalWrap>

      {!isActiveTab && (
        <IconButton
          className="absolute right-2 top-2 size-4"
          onClick={event => onRemove(event, route.pathname)}
        >
          <CloseOutlinedIcon className="!text-sm transition-all" />
        </IconButton>
      )}
    </li>
  );
};

export default memo(SortableTab);
